// Client-side markdown import/export helpers. Storage stays HTML end-to-end;
// these only convert at the edit boundary via the Markdown extension.

import flash from "@/lib/flashes"
import { normalizeMarkdownDocument } from "@/tiptap/core/markdown/markdown-normalize"

function sanitizeFilename(name) {
  const base = (name || "document")
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")

  return base || "document"
}

function downloadTextFile(filename, content, mimeType = "text/markdown") {
  if (typeof document === "undefined") return

  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function exportEditorMarkdown(editor, { filename = "document" } = {}) {
  if (typeof editor?.getMarkdown !== "function") return ""

  const markdown = editor.getMarkdown()
  downloadTextFile(`${sanitizeFilename(filename)}.md`, markdown, "text/markdown")

  return markdown
}

export function importMarkdownIntoEditor(editor, markdown) {
  if (!editor || typeof markdown !== "string") return false

  const manager = editor.markdown

  // Fall back to the extension command if the manager is unavailable.
  if (!manager || typeof manager.parse !== "function") {
    return editor
      .chain()
      .focus()
      .setContent(markdown, { contentType: "markdown" })
      .run()
  }

  // Parse markdown to JSON ourselves so we can normalize the document before
  // it hits the schema. The parser emits some nodes (e.g. images) inline that
  // are block-level in Hudu's schema, which would otherwise throw when set.
  try {
    const parsed = manager.parse(markdown)
    const normalized = normalizeMarkdownDocument(parsed, editor.schema)

    return editor.chain().focus().setContent(normalized).run()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Markdown import failed", error)
    flash("Could not import this markdown file.", "error")
    return false
  }
}

// `editor.isEmpty` is a getter on real Tiptap editors but can be a function in
// some call sites/mocks, so normalize both shapes.
function editorIsEmpty(editor) {
  return typeof editor?.isEmpty === "function" ? editor.isEmpty() : !!editor?.isEmpty
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"))
    reader.readAsText(file)
  })
}

// Opens a hidden file picker, reads the chosen markdown file, and replaces the
// editor content. Resolves to true when content was imported.
export function pickAndImportMarkdown(editor, { confirmReplace = true } = {}) {
  if (typeof document === "undefined" || !editor) return Promise.resolve(false)

  return new Promise((resolve) => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".md,.markdown,text/markdown"
    input.style.display = "none"

    input.addEventListener("change", async () => {
      const file = input.files?.[0]
      document.body.removeChild(input)

      if (!file) {
        resolve(false)
        return
      }

      const replaceConfirmed =
        !confirmReplace ||
        editorIsEmpty(editor) ||
        (typeof window !== "undefined" &&
          window.confirm("Replace the current content with the imported markdown file?"))

      if (!replaceConfirmed) {
        resolve(false)
        return
      }

      try {
        const text = await readFileAsText(file)
        resolve(importMarkdownIntoEditor(editor, text) !== false)
      } catch {
        resolve(false)
      }
    })

    document.body.appendChild(input)
    input.click()
  })
}

export { downloadTextFile, sanitizeFilename }
